import type { Date as PDate } from "../../generated/core/v1/date";
import { Province } from "../../generated/core/v1/address";
import { type Lender, LoanType } from "../../generated/core/v1/debts";
import { PriorityKind } from "../../generated/core/v1/family";
import { type GroupRRSPPlan, IncomeKind, PensionType } from "../../generated/core/v1/person";
import { PropertyKind } from "../../generated/core/v1/property";
import { PrioritiesSection_Frequency as Frequency } from "../../generated/core/v1/questionnaire";
import { isSet } from "../lib/dates";
import { newUuid } from "../lib/uuid";
import type { JobStart } from "./jobStart";

/**
 * The onboarding answers as the screens collect them, before they become a `core.v1.Questionnaire`
 * (see build.ts). Persisted in localStorage until the household is saved, so a reload or the sign-up
 * step in the middle of the flow loses nothing. Amounts are numbers (undefined = not answered).
 */
export type AccountKindKey =
  | "chequing"
  | "savings"
  | "credit"
  | "rrsp"
  | "grouprrsp"
  | "tfsa"
  | "fhsa"
  | "resp"
  | "nonreg";

export type DraftPerson = { uuid: string; givenName: string; familyName: string; dob?: PDate };

/**
 * A priority as onboarding and Priorities → Edit ask about it (priorities/PriorityEditor.tsx). "Buying
 * a home" (kind BUY_HOME) is the engine's home-buying goal: it asks the home's price, and `cost` is its
 * down payment (blank = 20% of the price, the engine's default); it never repeats.
 */
export type DraftPriority = {
  uuid: string;
  kind: PriorityKind;
  name: string;
  /** Kind BUY_HOME: the down payment. Otherwise roughly what it costs. Blank = "not sure". */
  cost?: number;
  /** Kind BUY_HOME only: the home's price (required). */
  housePrice?: number;
  when?: PDate;
  importance?: number;
  repeats: boolean;
  frequency: Frequency;
  until?: PDate;
  beneficiaryUuids: string[];
  /**
   * Onboarding: the education Abi added for this child (uuid), one per child under 18, named for them
   * and dated to the year they turn 18 until the date is changed (`whenEdited`).
   */
  forChild?: string;
  whenEdited?: boolean;
};

export type DraftAccount = {
  uuid: string;
  kind: AccountKindKey;
  name: string;
  ownerUuid: string;
  balance?: number;
  /** Credit cards: the interest rate (percent); chequing and savings: optional interest. */
  rate?: number;
  /** RESP only: the children it's for. */
  beneficiaryUuids: string[];
  /** Local import identity (Flinks); the questionnaire uses the client-minted uuid above. */
  flinks?: FlinksRef;
};

/** Which Flinks connection an imported item came from, and the bank's id for it. */
export type FlinksRef = { connectionId: string; accountId: string };

/** A bank connected through Flinks in onboarding: the institution, whose it is, what was left out. */
export type DraftFlinksConnection = {
  id: string;
  institution: string;
  ownerUuid: string;
  skipped: { name: string; reason: string }[];
};

export type DraftIncome = {
  uuid: string;
  memberUuid: string;
  kind: IncomeKind;
  /** "Name of income source" (a job's title, an income's name); blank = named by its kind. */
  name: string;
  amount?: number;
  groupRrspPlan?: GroupRRSPPlan; // jobs only
  /** "When did you start at ...?": asked (and sent) only for the job a DB pension is attached to. */
  start?: JobStart;
};

// job kinds, less self-employment
export const MATCHABLE_KINDS = new Set([
  IncomeKind.SALARY,
  IncomeKind.HOURLY,
  IncomeKind.COMMISSION,
  IncomeKind.BONUS,
]);

/**
 * The four kinds of income onboarding and Update Data ask about, one per way the engine treats income
 * (Ian, 2026-11-01): employment (a job, REGULAR_EMPLOYMENT: salary, hourly, commission, bonus),
 * self-employment (a job, SELF_EMPLOYMENT), other taxable income (rental, investment, pension, other:
 * taxed at year end) and tax-free income (government benefits, family support). Stored kinds map into
 * these; picking a type keeps the stored kind when it is already one of that type.
 */
export type IncomeType = "employment" | "self" | "taxable" | "taxfree";

export const INCOME_TYPE_LABEL: [IncomeType, string][] = [
  ["employment", "Employment"],
  ["self", "Self-employment or a business"],
  ["taxable", "Other taxable income"],
  ["taxfree", "Tax-free income"],
];

const INCOME_TYPE_KINDS: Record<IncomeType, IncomeKind[]> = {
  employment: [IncomeKind.SALARY, IncomeKind.HOURLY, IncomeKind.COMMISSION, IncomeKind.BONUS],
  self: [IncomeKind.SELF_EMPLOYED],
  taxable: [IncomeKind.OTHER, IncomeKind.RENTAL, IncomeKind.INVESTMENT, IncomeKind.PENSION],
  taxfree: [IncomeKind.GOVERNMENT_BENEFITS, IncomeKind.FAMILY_SUPPORT],
};

/** Each type's hint: what it covers. */
export const INCOME_TYPE_HINT: Record<IncomeType, string> = {
  employment: "Salary, hourly wages, commission or a bonus from an employer.",
  self: "Money you earn working for yourself.",
  taxable: "Rental income, investment income, a pension you already draw, or anything else taxed.",
  taxfree: "Government benefits such as the Canada Child Benefit, or regular support from family.",
};

export function incomeType(kind: IncomeKind): IncomeType {
  const hit = (Object.keys(INCOME_TYPE_KINDS) as IncomeType[]).find((t) =>
    INCOME_TYPE_KINDS[t].includes(kind),
  );
  return hit ?? "employment"; // unspecified is a salary (the engine's merge)
}

/** The kind to store for `type`: the current kind when it is already of that type. */
export function kindForType(type: IncomeType, current: IncomeKind): IncomeKind {
  return incomeType(current) === type ? current : INCOME_TYPE_KINDS[type][0];
}

/** Employment and self-employment are jobs (the only incomes a pension can be attached to). */
export const isJobKind = (kind: IncomeKind) => {
  const t = incomeType(kind);
  return t === "employment" || t === "self";
};
export const MATCH_RATES: [number, string][] = [
  [0, "No match"],
  [0.5, "50¢ per $1 (50%)"],
  [0.75, "75¢ per $1 (75%)"],
  [1, "Dollar for dollar (100%)"],
];
export const MATCH_CAPS: [number, string][] = Array.from({ length: 10 }, (_, k) => [
  (k + 1) / 100,
  `${k + 1}% of salary`,
]);
/**
 * The employee's own contribution rate, which can be above the employer's cap: 0 to 15%, the one list
 * onboarding, Update Data, and the check-in offer (the engine takes any rate from 0 to 100%).
 */
export const CONTRIBUTION_RATES: [number, string][] = [
  [0, "Nothing yet"],
  ...Array.from({ length: 15 }, (_, k): [number, string] => [(k + 1) / 100, `${k + 1}% of salary`]),
];

/**
 * The contribution rates to pick from (`CONTRIBUTION_RATES`), plus any rate given that isn't among
 * them (a stored 4.32%, an advised 3.44%), in order.
 */
export const contributionRateValues = (...extra: number[]): number[] =>
  [...new Set([...CONTRIBUTION_RATES.map(([v]) => v), ...extra])].sort((a, b) => a - b);

/**
 * A property as onboarding and Update Data both ask about it (components/OwedRows.tsx). A rate left
 * blank is "not sure": sent as not known, so the engine estimates it.
 */
export type DraftProperty = {
  uuid: string;
  kind: PropertyKind;
  /** Blank = named by its kind (the engine's defaults pass). */
  name: string;
  value?: number;
  propertyTax?: number;
  /** "Is there a mortgage on it?" Owing, rate, and its term only count on a yes. */
  hasMortgage: boolean;
  mortgageUuid: string;
  owing?: number;
  rate?: number;
  /** "Year you took it out" and "How many years it was for" (both or neither). */
  startYear?: number;
  termYears?: number;
  /**
   * A stored mortgage's terms not asked about here (Update Data): its remaining amortization in
   * months, or its payment. Left alone (not resent) until a year and a term replace them.
   */
  remainingMonths?: number;
  monthlyPayment?: number;
  lender?: Lender;
  /** Imported mortgages (Flinks); `created` = the import added the property, so removing it goes. */
  flinks?: FlinksRef & { created: boolean };
};

/** A debt other than a mortgage. Credit cards are entered with the accounts in onboarding. */
export type DraftDebt = {
  uuid: string;
  kind: LoanType;
  name: string;
  balance?: number;
  rate?: number;
  /** Stored values not asked about here (Update Data), kept when it's resent. */
  memberUuid?: string;
  monthlyPayment?: number;
  creditLimit?: number;
  /** Local import identity (Flinks). */
  flinks?: FlinksRef;
};

export type DraftEmergency = { accountUuid: string; whole: boolean; amount?: number };

export type PartnerStatus = "married" | "commonlaw" | "single";

/** An adult's "Is there a workplace pension?" (Income step), attached to one of their jobs. */
export type DraftPension = { uuid: string; has: boolean; type: PensionType; jobUuid: string };

export type OnboardingDraft = {
  version: 3;
  step: string;
  self: DraftPerson;
  email: string;
  province: Province;
  partnerStatus: PartnerStatus;
  partner: DraftPerson;
  hasKids: boolean;
  kids: DraftPerson[];
  priorities: DraftPriority[];
  /** Children whose education Abi added and the household removed (not added again). */
  dismissedEducation: string[];
  retirementImportance?: number;
  accounts: DraftAccount[];
  noInvestments: boolean;
  spending: { essential?: number; lifestyle?: number; rent?: number };
  incomes: DraftIncome[];
  /** "Do you own any property?" Rent is dropped only when one is the principal residence. */
  hasProperty: boolean;
  properties: DraftProperty[];
  /**
   * "I'm a first-time home buyer", by adult uuid (`Person.is_first_time_home_buyer`), asked beside
   * the properties whether or not they own one. Unset = unchecked.
   */
  firstTimeBuyer: Record<string, boolean>;
  hasDebts: boolean;
  debts: DraftDebt[];
  /** Empty = "None of it". */
  emergency: DraftEmergency[];
  /** By adult uuid; unset = no. */
  pensions: Record<string, DraftPension>;
  /**
   * "Retiring comfortably" (always on): the share of today's income wanted at retirement and the
   * age, sent as `RetirementSection{TARGET, income_replacement_percent, retirement_age}`.
   */
  retirement: { pct: number; age: number };
  /** Banks connected through Flinks on the Banking step. */
  flinksConnections: DraftFlinksConnection[];
};

export const ACCOUNT_KIND_LABEL: Record<AccountKindKey, string> = {
  chequing: "Chequing",
  savings: "Savings",
  credit: "Credit card",
  rrsp: "RRSP",
  grouprrsp: "Group RRSP (through work)",
  tfsa: "TFSA",
  fhsa: "FHSA",
  resp: "RESP",
  nonreg: "Non-registered (trading)",
};

export const EVERYDAY_KINDS: AccountKindKey[] = ["chequing", "savings", "credit"];
export const INVESTMENT_KINDS: AccountKindKey[] = [
  "rrsp",
  "grouprrsp",
  "tfsa",
  "fhsa",
  "resp",
  "nonreg",
];

/**
 * The prototype's common priorities (v47's list, less "Leave an inheritance", which the engine has
 * no kind for), in its order, with their kinds. Each arrives filled in with how it usually repeats
 * (v39 `PRI_DEF`); everything stays editable. A child's education isn't here: Abi adds it per child.
 */
export const COMMON_PRIORITIES: {
  kind: PriorityKind;
  name: string;
  repeats?: boolean;
  frequency?: Frequency;
}[] = [
  { kind: PriorityKind.BUY_HOME, name: "Buying a home" },
  { kind: PriorityKind.BIGGER_HOME, name: "A bigger home" },
  { kind: PriorityKind.RENOVATION, name: "Home renovation" },
  {
    kind: PriorityKind.TRAVEL,
    name: "Travel every year",
    repeats: true,
    frequency: Frequency.YEARLY,
  },
  { kind: PriorityKind.WEDDING, name: "A wedding" },
  { kind: PriorityKind.START_BUSINESS, name: "Starting a business" },
  {
    kind: PriorityKind.NEW_CAR,
    name: "A new car",
    repeats: true,
    frequency: Frequency.EVERY_5_YEARS,
  },
  {
    kind: PriorityKind.HELP_FAMILY,
    name: "Helping family",
    repeats: true,
    frequency: Frequency.MONTHLY,
  },
  {
    kind: PriorityKind.GIVING,
    name: "Giving to causes",
    repeats: true,
    frequency: Frequency.YEARLY,
  },
];

export const FREQUENCY_LABEL: [Frequency, string][] = [
  [Frequency.YEARLY, "Every year"],
  [Frequency.EVERY_2_YEARS, "Every 2 years"],
  [Frequency.EVERY_3_YEARS, "Every 3 years"],
  [Frequency.EVERY_5_YEARS, "Every 5 years"],
  [Frequency.MONTHLY, "Every month"],
];

export const INCOME_KIND_LABEL: [IncomeKind, string][] = [
  [IncomeKind.SALARY, "Salary"],
  [IncomeKind.HOURLY, "Hourly wages"],
  [IncomeKind.SELF_EMPLOYED, "Self-employed or business"],
  [IncomeKind.COMMISSION, "Commission"],
  [IncomeKind.BONUS, "Bonus"],
  [IncomeKind.RENTAL, "Rental income"],
  [IncomeKind.INVESTMENT, "Investment income"],
  [IncomeKind.PENSION, "Pension income"],
  [IncomeKind.GOVERNMENT_BENEFITS, "Government benefits"],
  [IncomeKind.FAMILY_SUPPORT, "Regular support from family"],
  [IncomeKind.OTHER, "Other"],
];

export const PROPERTY_KIND_LABEL: [PropertyKind, string][] = [
  [PropertyKind.PRINCIPAL_RESIDENCE, "Principal residence"],
  [PropertyKind.RENTAL, "Rental property"],
  [PropertyKind.COTTAGE, "Cottage or vacation home"],
  [PropertyKind.SECONDARY_HOME, "Secondary residence"],
  [PropertyKind.INVESTMENT, "Investment property"],
  [PropertyKind.LAND, "Land"],
  [PropertyKind.FAMILY_MEMBER_HOME, "Property for a family member"],
  [PropertyKind.OTHER, "Other"],
];

/** "What is it" on the debts step (credit cards are entered with the accounts). */
export const DEBT_KIND_LABEL: [LoanType, string][] = [
  [LoanType.LINE_OF_CREDIT, "Line of credit"],
  [LoanType.CREDIT_CARD, "Credit card"],
  [LoanType.CAR_LOAN, "Car loan"],
  [LoanType.STUDENT_LOAN, "Student loan"],
  [LoanType.PERSONAL_LOAN, "Personal loan"],
  [LoanType.CRA_DEBT, "Money owed to the CRA"],
  [LoanType.FAMILY_LOAN, "Loan from family"],
  [LoanType.BUY_NOW_PAY_LATER, "Buy now, pay later"],
  [LoanType.BUSINESS_LOAN, "Business loan"],
  [LoanType.OTHER, "Other"],
];

export const PROVINCES: [Province, string][] = [
  [Province.ONTARIO, "Ontario"],
  [Province.BRITISH_COLUMBIA, "British Columbia"],
  [Province.ALBERTA, "Alberta"],
  [Province.SASKATCHEWAN, "Saskatchewan"],
  [Province.MANITOBA, "Manitoba"],
  [Province.QUEBEC, "Quebec"],
  [Province.NEW_BRUNSWICK, "New Brunswick"],
  [Province.NOVA_SCOTIA, "Nova Scotia"],
  [Province.PRINCE_EDWARD_ISLAND, "Prince Edward Island"],
  [Province.NEWFOUNDLAND_AND_LABRADOR, "Newfoundland and Labrador"],
  [Province.YUKON, "Yukon"],
  [Province.NORTHWESTERN_TERRITORIES, "Northwest Territories"],
  [Province.NUNAVUT, "Nunavut"],
];

/** The share of today's income wanted at retirement: 50% to 200%, in tens (v39). */
export const PCT_OPTIONS = Array.from({ length: 16 }, (_, k) => 50 + k * 10);
/** The share preselected for a new household (v38's preselected target). */
export const DEFAULT_RETIREMENT_PCT = 70;
export const AGE_OPTIONS = Array.from({ length: 26 }, (_, k) => 50 + k);

export function newPerson(): DraftPerson {
  return { uuid: newUuid(), givenName: "", familyName: "" };
}

export function newPriority(
  kind: PriorityKind,
  name: string,
  repeats = false,
  frequency = Frequency.YEARLY,
): DraftPriority {
  return {
    uuid: newUuid(),
    kind,
    name,
    repeats,
    frequency,
    beneficiaryUuids: [],
  };
}

/** A priority of the same name is already on the list (adding it again is refused, v39). */
export function hasPriorityNamed(list: DraftPriority[], name: string): boolean {
  const n = name.trim().toLowerCase();
  return list.some((p) => p.name.trim().toLowerCase() === n);
}

/** "Riley’s education" (or "Child 2’s education" before the name is in). */
export function educationName(kid: DraftPerson, i: number): string {
  return `${kid.givenName.trim() || `Child ${i + 1}`}\u2019s education`;
}

/**
 * The education priorities Abi adds (v39): one per child still under 18 on `today` (a child with no
 * birthday yet counts), named for them and dated to the year they turn 18 unless the household changed
 * the date. Ones for a child who is gone or 18 go; ones the household removed (`dismissed`) aren't
 * added again. Returns the same list when nothing changes.
 */
export function syncEducation(
  list: DraftPriority[],
  kids: DraftPerson[],
  today: PDate,
  dismissed: string[] = [],
): DraftPriority[] {
  const under18 = kids
    .map((k, i) => ({ k, i }))
    .filter(({ k }) => !isSet(k.dob) || k.dob.year + 18 > today.year);
  const want = new Map(under18.map(({ k, i }) => [k.uuid, { k, i }]));
  let changed = false;
  const out: DraftPriority[] = [];
  for (const p of list) {
    if (!p.forChild) {
      out.push(p);
      continue;
    }
    const hit = want.get(p.forChild);
    if (!hit) {
      changed = true;
      continue;
    }
    const name = educationName(hit.k, hit.i);
    const turns = isSet(hit.k.dob) ? { year: hit.k.dob.year + 18, month: 0, day: 0 } : undefined;
    const when = p.whenEdited ? p.when : turns;
    const sameWhen =
      (when?.year ?? 0) === (p.when?.year ?? 0) &&
      (when?.month ?? 0) === (p.when?.month ?? 0) &&
      (when?.day ?? 0) === (p.when?.day ?? 0);
    if (name !== p.name || !sameWhen) {
      changed = true;
      out.push({ ...p, name, when });
    } else out.push(p);
  }
  for (const { k, i } of under18) {
    if (dismissed.includes(k.uuid) || out.some((p) => p.forChild === k.uuid)) continue;
    changed = true;
    out.push({
      ...newPriority(PriorityKind.CHILD_EDUCATION, educationName(k, i)),
      beneficiaryUuids: [k.uuid],
      forChild: k.uuid,
      when: isSet(k.dob) ? { year: k.dob.year + 18, month: 0, day: 0 } : undefined,
    });
  }
  return changed ? out : list;
}

export function newAccount(kind: AccountKindKey, ownerUuid: string): DraftAccount {
  return { uuid: newUuid(), kind, name: "", ownerUuid, beneficiaryUuids: [] };
}

export function newIncome(memberUuid: string): DraftIncome {
  return { uuid: newUuid(), memberUuid, kind: IncomeKind.SALARY, name: "" };
}

export function newPension(): DraftPension {
  return { uuid: newUuid(), has: false, type: PensionType.DEFINED_BENEFIT, jobUuid: "" };
}

/**
 * A new property row: the first is taken to be the principal residence and the others rentals until
 * the household picks otherwise; no mortgage until it says Yes (v43: every gate starts at No).
 */
export function newProperty(first: boolean): DraftProperty {
  return {
    uuid: newUuid(),
    kind: first ? PropertyKind.PRINCIPAL_RESIDENCE : PropertyKind.RENTAL,
    name: "",
    hasMortgage: false,
    mortgageUuid: newUuid(),
  };
}

export function newDebt(): DraftDebt {
  return { uuid: newUuid(), kind: LoanType.LINE_OF_CREDIT, name: "" };
}

export function emptyDraft(): OnboardingDraft {
  const self = newPerson();
  return {
    version: 3,
    step: "welcome",
    self,
    email: "",
    province: Province.ONTARIO,
    partnerStatus: "single",
    partner: newPerson(),
    hasKids: false,
    kids: [],
    priorities: [],
    dismissedEducation: [],
    accounts: [],
    noInvestments: false,
    spending: {},
    incomes: [],
    hasProperty: false,
    properties: [],
    firstTimeBuyer: {},
    hasDebts: false,
    debts: [],
    emergency: [],
    pensions: {},
    retirement: { pct: DEFAULT_RETIREMENT_PCT, age: DEFAULT_AGE },
    flinksConnections: [],
  };
}

/** The retirement age preselected (the engine's default, `DEFAULT_RETIREMENT_AGE`). */
const DEFAULT_AGE = 65;

/** The adults in the household: you, and your partner unless "Just me". */
export function members(draft: OnboardingDraft): DraftPerson[] {
  return draft.partnerStatus === "single" ? [draft.self] : [draft.self, draft.partner];
}

export function personLabel(p: DraftPerson, fallback: string): string {
  return p.givenName.trim() || fallback;
}

/**
 * The job an adult's pension is attached to, as the Income step shows it: the one picked, else
 * their first job (employment or self-employment); undefined without one.
 */
export function pensionJob(draft: OnboardingDraft, memberUuid: string): DraftIncome | undefined {
  const jobs = draft.incomes.filter((i) => i.memberUuid === memberUuid && isJobKind(i.kind));
  return jobs.find((i) => i.uuid === draft.pensions[memberUuid]?.jobUuid) ?? jobs[0];
}

/** Whether the adult said Yes to a defined benefit pension (the start question is asked then). */
export function hasDbPension(draft: OnboardingDraft, memberUuid: string): boolean {
  const pn = draft.pensions[memberUuid];
  return !!pn?.has && pn.type === PensionType.DEFINED_BENEFIT;
}

// ------------------------------------------------------------------ persistence

const STORAGE_KEY = "abi.onboardingDraft";

/**
 * The steps a draft saved signed out can be on (every later step needs an account): sign-up is on
 * the household step; "priorities" and "account" are from the order before 2026-11-01 (priorities,
 * then a sign-up step), whose signed-out drafts are still anyone's.
 */
const SIGNED_OUT_STEPS = new Set(["welcome", "household", "priorities", "account"]);

/** As stored: the draft, and the Firebase uid of the user who saved it (none when signed out). */
type Stored = (OnboardingDraft | DraftV2 | DraftV1) & { owner?: string };

/**
 * The draft on this device, if it's `uid`'s: one another user saved on the same browser isn't
 * loaded (null: start afresh). A draft saved signed out (before sign-up) is anyone's, the next user
 * to sign up takes it; one without an owner past the sign-up step was saved before drafts had
 * owners, and could be anyone's, so it isn't loaded either.
 */
export function loadDraft(uid?: string): OnboardingDraft | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Stored;
    if (!stored) return null;
    const { owner, ...parsed } = stored;
    if (owner ? owner !== uid : uid && !SIGNED_OUT_STEPS.has(parsed.step)) return null;
    if (parsed.version === 1) return named(upgradeV2({ ...emptyV2(), ...upgradeV1(parsed) }));
    if (parsed.version === 2) return named(upgradeV2({ ...emptyV2(), ...parsed }));
    if (parsed.version !== 3) return null;
    return named({ ...emptyDraft(), ...parsed });
  } catch {
    return null;
  }
}

/** Drafts saved before properties, debts and incomes had names get blank ones. */
function named(d: OnboardingDraft): OnboardingDraft {
  return {
    ...d,
    properties: d.properties.map((p) => ({ ...p, name: p.name ?? "" })),
    debts: d.debts.map((x) => ({ ...x, name: x.name ?? "" })),
    incomes: d.incomes.map((x) => ({ ...x, name: x.name ?? "" })),
  };
}

/**
 * Version 2 had a retirement step ("work it out from what I spend" or a target), one household
 * pension question, and a mortgage's years left. Version 3 asks the share and the age on "Retiring
 * comfortably", a pension per adult, and a mortgage's start year and term (years left is dropped:
 * it isn't asked any more).
 */
type DraftV2 = Omit<
  OnboardingDraft,
  "version" | "pensions" | "retirement" | "dismissedEducation"
> & {
  version: 2;
  hasPension?: boolean;
  pensionType?: PensionType;
  pensionMemberUuid?: string;
  pensionUuid?: string;
  retirement?: { mode?: "calc" | "pick"; pct?: number; age?: number };
};

function emptyV2(): DraftV2 {
  const { pensions: _p, dismissedEducation: _d, retirement: _r, ...rest } = emptyDraft();
  return { ...rest, version: 2 };
}

export function upgradeV2(v2: DraftV2): OnboardingDraft {
  const { hasPension, pensionType, pensionMemberUuid, pensionUuid, retirement, ...rest } = v2;
  const owner = pensionMemberUuid || v2.self.uuid;
  return {
    ...rest,
    version: 3,
    dismissedEducation: [],
    pensions: hasPension
      ? {
          [owner]: {
            uuid: pensionUuid || newUuid(),
            has: true,
            type: pensionType || PensionType.DEFINED_BENEFIT,
            jobUuid: "",
          },
        }
      : {},
    retirement: {
      pct: retirement?.mode === "pick" && retirement.pct ? retirement.pct : DEFAULT_RETIREMENT_PCT,
      age: retirement?.age ?? DEFAULT_AGE,
    },
    properties: v2.properties.map((p) => {
      const { yearsLeft: _y, ...prop } = p as DraftProperty & { yearsLeft?: number };
      return prop;
    }),
  };
}

/**
 * Version 1 asked "Do you have a mortgage?" for the whole household, and every property it kept had
 * one. Version 2 asks "Do you own any property?" and the mortgage on each property.
 */
type DraftV1 = Omit<DraftV2, "version" | "hasProperty" | "properties"> & {
  version: 1;
  hasMortgage?: boolean;
  properties?: Omit<DraftProperty, "hasMortgage" | "name">[];
};

export function upgradeV1(v1: DraftV1): DraftV2 {
  const { hasMortgage, properties, ...rest } = v1;
  return {
    ...rest,
    version: 2,
    hasProperty: !!hasMortgage,
    properties: (properties ?? []).map((p) => ({ ...p, name: "", hasMortgage: true })),
  };
}

/** Keep the draft on this device, as `uid`'s (signed out: anyone's, see `loadDraft`). */
export function saveDraft(draft: OnboardingDraft, uid?: string): void {
  try {
    const stored: Stored = uid ? { ...draft, owner: uid } : draft;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // storage full or disabled: the draft only lives in memory
  }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
